import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from '../../../core/common/collapse-header/collapse-header'
import {
  getProjects,
  getTasksByProjectId,
  deleteTask,
  type Project,
  type Tasking,
} from "../../../core/api/useTask";
import {
  fetchProjectEmployees,
  type Employee,
} from "../../../core/api/useProjectMapping";
import { API_BASE_PATH } from "../../../environment";
import Dropdown from "react-bootstrap/Dropdown";
import { Modal, Button } from "react-bootstrap";
import AddTaskModal from "./AddTaskModal";
import { useSelector } from "react-redux";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";

const Task = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const itemsPerPage = 2;
  const thisID = location.state;
  const thisRole = useSelector((state: any) => state.auth.role);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectTasks, setProjectTasks] = useState<Tasking[]>([]);
    const empToken = useAuthToken();
  const [employeeDetailsMap, setEmployeeDetailsMap] = useState<
    Record<string, Employee>
  >({});
  const [loading, setLoading] = useState(true);
  const [taskToDelete, setTaskToDelete] = useState<Tasking | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<
    "all" | "critical" | "high" | "medium" | "low"
  >("all");
  const [isFiltering, setIsFiltering] = useState(false);

  const paginatedProjects = projects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const projectsData = await getProjects(empToken);
        setProjects(projectsData);
        const firstProject = projectsData[0];
        if (firstProject) {
          setSelectedProject(firstProject);
          const tasks = await getTasksByProjectId(empToken,firstProject.id);
          setProjectTasks(tasks);
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const employees = await fetchProjectEmployees();
        const map: Record<string, Employee> = {};
        employees.forEach((emp) => {
          map[emp.employee_id] = emp;
        });
        setEmployeeDetailsMap(map);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    loadEmployees();
  }, []);

  useEffect(() => {
    if (selectedProject?.id) {
      fetchTasks(selectedProject.id);
    } else {
      setProjectTasks([]);
    }
  }, [selectedProject]);

  const fetchTasks = async (projectId: string) => {
    setLoading(true);
    try {
      const tasks = await getTasksByProjectId(empToken,projectId);
      setProjectTasks(tasks);
    } catch (err) {
      console.error("Failed to fetch tasks", err);
      setProjectTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = async (project: Project) => {
    setSelectedProject(project);
    try {
      const tasks = await getTasksByProjectId(empToken,project.id);
      setProjectTasks(tasks);
    } catch (err) {
      console.error("Failed to load tasks for project", err);
    }
  };

  const handleTaskDelete = async (taskId: string) => {
    try {
      await deleteTask(empToken,taskId);
      if (selectedProject?.id) await fetchTasks(selectedProject.id);
    } catch (err) {
      console.error("Error deleting task", err);
    } finally {
      setTaskToDelete(null);
    }
  };

  const handlePriorityChange = (
    newPriority: "all" | "high" | "medium" | "low"
  ) => {
    setIsFiltering(true);
    setTimeout(() => {
      setPriorityFilter(newPriority);
      setIsFiltering(false);
    }, 500);
  };
  type SortOption =
    | "due-asc"
    | "due-desc"
    | "priority-high-low"
    | "priority-low-high";
  const [sortOption, setSortOption] = useState<SortOption>("due-asc");

  const getFilteredTasks = (): Tasking[] => {
    let filtered = [...projectTasks];

    // 1. Apply priority filter
    if (priorityFilter !== "all") {
      filtered = filtered.filter(
        (task) => task.priority?.toLowerCase() === priorityFilter
      );
    }

    // 2. Apply sorting
    switch (sortOption) {
      case "due-asc":
        filtered.sort(
          (a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
        );
        break;
      case "due-desc":
        filtered.sort(
          (a, b) =>
            new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()
        );
        break;
      case "priority-high-low":
        filtered.sort(
          (a, b) => priorityValue(b.priority) - priorityValue(a.priority)
        );
        break;
      case "priority-low-high":
        filtered.sort(
          (a, b) => priorityValue(a.priority) - priorityValue(b.priority)
        );
        break;
    }

    return filtered;
  };

  const priorityValue = (priority?: string) => {
    switch ((priority || "").toLowerCase()) {
      case "critical":
        return 4;
      case "high":
        return 3;
      case "medium":
        return 2;
      case "low":
        return 1;
      default:
        return 0;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-success text-white";
      case "in progress":
        return "bg-warning text-dark";
      case "pending":
        return "bg-secondary text-white";
      default:
        return "bg-light text-dark";
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "critical":
        return "bg-danger text-white";
      case "high":
        return "bg-danger-subtle text-danger";
      case "medium":
        return "bg-warning-subtle text-dark";
      case "low":
        return "bg-info-subtle text-dark";
      default:
        return "bg-light text-dark";
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Tasks</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Projects</li>
                <li className="breadcrumb-item active">Tasks</li>
              </ol>
            </nav>
          </div>
          <div className="my-xl-auto right-content d-flex">
            <div className="mb-2">
              <AddTaskModal />
            </div>
            <div className="head-icons ms-2 mb-0">
              <CollapseHeader />
            </div>
          </div>
        </div>

        <div className="row">
          {/* Project List */}
          <div className="col-xl-4 col-lg-5 mb-4">
            <div
              className="bg-white rounded shadow-sm p-3 h-100 d-flex flex-column"
              style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
            >
              {paginatedProjects.map((project) => (
                <div className="card mb-3" key={project.id}>
                  <div className="card-body">
                    <div className="d-flex align-items-center pb-3 mb-3 border-bottom">
                      <Link
                        to={all_routes.tasksdetails}
                        className="flex-shrink-0 me-2"
                      >
                        <img
                          className="avatar avatar-lg rounded-circle me-1"
                          src={`${API_BASE_PATH}/project/project/${project.id}/image`}
                          alt="/assets/img/defaultavatar.png"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src = "/assets/img/defaultavatar.png";
                          }}
                        />
                      </Link>
                      <div>
                        <h6 className="mb-1 d-flex flex-column">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleProjectClick(project);
                            }}
                            className="btn btn-link p-0 text-start text-decoration-none fw-bold text-primary"
                          >
                            {project.name}
                          </button>
                          <span className="fs-12 fw-normal text-muted">
                            {project.type?.toUpperCase()}
                          </span>
                        </h6>
                        <div className="d-flex align-items-center">
                          <span>{project.projectMemberIds.length} tasks</span>
                          <span className="mx-1">
                            <i className="ti ti-point-filled text-primary" />
                          </span>
                          <span>{project.projectLeadIds.length} Completed</span>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-sm-4 mb-3">
                        <span className="mb-1 d-block">Deadline</span>
                        <p className="text-dark">{project.deadline}</p>
                      </div>
                      <div className="col-sm-4 mb-3">
                        <span className="mb-1 d-block">Value</span>
                        <p className="text-dark">
                          ${project.value.toLocaleString()}
                        </p>
                      </div>
                      <div className="col-sm-4 mb-3">
                        <span className="mb-1 d-block">Project Lead</span>
                        {project.projectManagerIds.map((id) => {
                          const emp = employeeDetailsMap[id];
                          return (
                            <h6
                              key={id}
                              className="fw-normal d-flex align-items-center flex-wrap mb-1"
                            >
                              <img
                                className="avatar avatar-xs rounded-circle me-1"
                                src={`${API_BASE_PATH}/register/employee/${emp?.employee_id}/image`}
                                alt="/assets/img/defaultavatar.png"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.onerror = null;
                                  target.src = "/assets/img/defaultavatar.png";
                                }}
                              />
                              <span style={{ wordBreak: "break-word" }}>
                                {emp?.username || "Unknown"}
                              </span>
                            </h6>
                          );
                        })}
                      </div>
                    </div>
                    <div className="bg-light p-2 rounded">
                      <div className="row align-items-center">
                        <div className="col-6">
                          <span className="fw-medium d-flex align-items-center">
                            <i className="ti ti-clock text-primary me-2" />
                            Total {project.projectManagerIds.length * 100} Hrs
                          </span>
                        </div>
                        <div className="col-6">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <small className="text-dark">
                              {project.progress}% Completed
                            </small>
                          </div>
                          <div className="progress progress-xs">
                            <div
                              className="progress-bar bg-info"
                              role="progressbar"
                              style={{ width: `${project.progress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {!loading && (
                <div className="d-flex justify-content-center mt-3">
                  <div className="btn-group">
                    <button
                      className="btn btn-outline-primary"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    {Array.from(
                      { length: Math.ceil(projects.length / itemsPerPage) },
                      (_, i) => i + 1
                    ).map((page) => (
                      <button
                        key={page}
                        className={`btn ${currentPage === page
                            ? "btn-primary"
                            : "btn-outline-primary"
                          }`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="btn btn-outline-primary"
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(
                            prev + 1,
                            Math.ceil(projects.length / itemsPerPage)
                          )
                        )
                      }
                      disabled={currentPage * itemsPerPage >= projects.length}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Task List */}
          <div className="col-xl-8">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    {selectedProject
                      ? selectedProject.name
                      : "Select a project"}
                  </h5>
                  <div className="btn-group">
                    {["all", "critical", "high", "medium", "low"].map(
                      (level) => (
                        <button
                          key={level}
                          className={`btn btn-sm ${priorityFilter === level
                              ? "btn-primary"
                              : "btn-outline-primary"
                            }`}
                          onClick={() => handlePriorityChange(level as any)}
                        >
                          {level.charAt(0).toUpperCase() + level.slice(1)}
                        </button>
                      )
                    )}
                  </div>
                  <Dropdown className="ms-3">
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      size="sm"
                      id="sort-dropdown"
                    >
                      Sort By
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => setSortOption("due-asc")}>
                        Due Date ↑
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => setSortOption("due-desc")}>
                        Due Date ↓
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => setSortOption("priority-high-low")}
                      >
                        Priority: Critical → Low
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => setSortOption("priority-low-high")}
                      >
                        Priority: Low → Critical
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {selectedProject && (
                  <div className="bg-light p-2 rounded mb-3">
                    <span className="d-block mb-1">Tasks Done</span>
                    <h4>
                      {
                        projectTasks.filter(
                          (t) => t.status?.toLowerCase() === "completed"
                        ).length
                      }{" "}
                      / {projectTasks.length}
                    </h4>
                    <div className="progress progress-xs mb-2">
                      <div
                        className="progress-bar bg-info"
                        role="progressbar"
                        style={{
                          width: `${projectTasks.length
                              ? Math.round(
                                (projectTasks.filter(
                                  (t) =>
                                    t.status?.toLowerCase() === "completed"
                                ).length /
                                  projectTasks.length) *
                                100
                              )
                              : 0
                            }%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="list-group list-group-flush">
                  {isFiltering ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : getFilteredTasks().length === 0 ? (
                    <div className="text-center py-5">
                      <h5 className="text-muted mb-2">No tasks found</h5>
                      <p className="text-secondary">
                        Try selecting another priority.
                      </p>
                    </div>
                  ) : (
                    getFilteredTasks().map((task) => (
                      <div
                        className="list-group-item shadow-sm rounded mb-2 p-3"
                        key={task.taskId}
                      >
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={
                                task.status?.toLowerCase() === "completed"
                              }
                              readOnly
                            />
                            <span>{task.taskName}</span>
                            <span className="badge bg-secondary">
                              <i className="ti ti-calendar me-1" />
                              {task.dueDate}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            {task.priority && (
                              <span
                                className={`badge text-capitalize ${getPriorityBadgeClass(
                                  task.priority
                                )}`}
                              >
                                {task.priority}
                              </span>
                            )}
                            <span className="badge bg-soft-pink">
                              {task.status}
                            </span>
                            <img
                              className="avatar avatar-sm rounded-circle"
                              src={`${API_BASE_PATH}/register/employee/${task.employee.employeeId}/image`}
                              alt="/assets/img/defaultavatar.png"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = "/assets/img/defaultavatar.png";
                              }}
                            />
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                as="button"
                                className="bg-transparent border-0 text-primary"
                              />
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  onClick={() =>
                                    navigate(all_routes.tasksdetails, {
                                      state: { taskId: task.taskId },
                                    })
                                  }
                                >
                                  <i className="ti ti-eye me-2" />
                                  View
                                </Dropdown.Item>
                                <Dropdown.Item
                                  className="text-danger"
                                  onClick={() => setTaskToDelete(task)}
                                >
                                  <i className="ti ti-trash me-2" />
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>

                          <Modal
                            show={taskToDelete?.taskId === task.taskId}
                            onHide={() => setTaskToDelete(null)}
                            centered
                          >
                            <Modal.Header closeButton>
                              <Modal.Title>Confirm Delete</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                              Are you sure you want to delete{" "}
                              <strong>{task.taskName}</strong>?
                            </Modal.Body>
                            <Modal.Footer>
                              <Button
                                variant="secondary"
                                onClick={() => setTaskToDelete(null)}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="danger"
                                onClick={() => handleTaskDelete(task.taskId)}
                              >
                                Delete
                              </Button>
                            </Modal.Footer>
                          </Modal>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Task;
